import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import {
  User,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterWithCodeRequest,
  RegisterCreateTenantRequest,
  RegisterResponse,
  AuthState,
  AuthPayload,
} from '../models';
import { ApiService } from './api.service';
import { TokenStoreService } from './token-store.service';

/**
 * sessionStorage key for the non-sensitive session data persisted across
 * page reloads. Stores { user, refreshToken } only — the access token is
 * kept in memory (TokenStoreService) and never written to any storage.
 */
const SESSION_KEY = 'g8r_session';

const EMPTY_STATE: AuthState = {
  user: null,
  accessToken: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private authState$ = new BehaviorSubject<AuthState>(EMPTY_STATE);

  constructor(
    private apiService: ApiService,
    private tokenStore: TokenStoreService,
  ) {}

  /**
   * Attempt to restore the previous session using a stored refresh token.
   * Returns an Observable that emits `{ user, accessToken }` on success, or
   * `null` if there is no stored session or the refresh token has expired.
   *
   * Callers (e.g. AppComponent) can subscribe to dispatch a hydrateSession
   * NgRx action when successful.
   */
  initSession(): Observable<{ user: User; accessToken: string } | null> {
    const stored = this.readSession();
    if (!stored?.refreshToken) return of(null);

    this.authState$.next({ ...EMPTY_STATE, isLoading: true });

    return this.apiService.refreshToken(stored.refreshToken).pipe(
      tap(({ accessToken, refreshToken }) => {
        this.tokenStore.set(accessToken);
        this.writeSession({ user: stored.user, refreshToken });
        this.authState$.next({
          user: stored.user,
          accessToken,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      }),
      map(({ accessToken }) => ({ user: stored.user, accessToken })),
      catchError(() => {
        this.clearSession();
        return of(null);
      }),
    );
  }

  register(request: RegisterRequest): Observable<RegisterResponse> {
    this.setLoading(true);
    return this.apiService.register(request).pipe(
      tap((response: RegisterResponse) => {
        this.setAuthentication({ user: response.user, accessToken: response.accessToken, refreshToken: response.refreshToken });
        this.setLoading(false);
      }),
      catchError((error) => {
        this.setError(error.error?.message || 'Registration failed');
        this.setLoading(false);
        throw error;
      }),
    );
  }

  registerWithCode(request: RegisterWithCodeRequest): Observable<RegisterResponse> {
    this.setLoading(true);
    return this.apiService.registerWithCode(request).pipe(
      tap((response: RegisterResponse) => {
        this.setAuthentication({ user: response.user, accessToken: response.accessToken, refreshToken: response.refreshToken });
        this.setLoading(false);
      }),
      catchError((error) => {
        this.setError(error.error?.message || 'Registration with code failed');
        this.setLoading(false);
        throw error;
      }),
    );
  }

  registerCreateTenant(request: RegisterCreateTenantRequest): Observable<RegisterResponse> {
    this.setLoading(true);
    return this.apiService.registerCreateTenant(request).pipe(
      tap((response: RegisterResponse) => {
        this.setAuthentication({ user: response.user, accessToken: response.accessToken, refreshToken: response.refreshToken });
        this.setLoading(false);
      }),
      catchError((error) => {
        this.setError(error.error?.message || 'Tenant creation failed');
        this.setLoading(false);
        throw error;
      }),
    );
  }

  login(request: LoginRequest): Observable<LoginResponse> {
    this.setLoading(true);
    return this.apiService.login(request).pipe(
      tap((response: LoginResponse) => {
        if (!response.requiresMfa) {
          this.setAuthentication({ user: response.user, accessToken: response.accessToken, refreshToken: response.refreshToken });
        } else {
          this.authState$.next({
            ...this.authState$.value,
            user: response.user,
            mfaRequired: true,
            isLoading: false,
            error: null,
          });
        }
        this.setLoading(false);
      }),
      catchError((error) => {
        this.setError(error.error?.message || 'Login failed');
        this.setLoading(false);
        throw error;
      }),
    );
  }

  logout(): void {
    const stored = this.readSession();
    if (stored?.refreshToken) {
      // Fire-and-forget — clear state immediately; backend revocation happens async
      this.apiService.logout(stored.refreshToken).pipe(
        catchError(() => of(null)),
      ).subscribe();
    }
    this.clearSession();
  }

  getAuthState(): Observable<AuthState> {
    return this.authState$.asObservable();
  }

  getCurrentUser(): User | null {
    return this.authState$.value.user;
  }

  getAccessToken(): string | null {
    return this.tokenStore.get();
  }

  isAuthenticated(): boolean {
    return this.authState$.value.isAuthenticated;
  }

  isLoading(): boolean {
    return this.authState$.value.isLoading;
  }

  getError(): string | null {
    return this.authState$.value.error;
  }

  getTenantId(): string | null {
    return this.authState$.value.user?.tenantId || null;
  }

  private setAuthentication(payload: AuthPayload): void {
    this.tokenStore.set(payload.accessToken);
    if (payload.refreshToken) {
      this.writeSession({ user: payload.user, refreshToken: payload.refreshToken });
    }
    this.authState$.next({
      user: payload.user,
      accessToken: payload.accessToken,
      isAuthenticated: true,
      isLoading: false,
      error: null,
    });
  }

  private clearSession(): void {
    sessionStorage.removeItem(SESSION_KEY);
    this.tokenStore.set(null);
    this.authState$.next(EMPTY_STATE);
  }

  private readSession(): { user: User; refreshToken: string } | null {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as { user: User; refreshToken: string };
    } catch {
      return null;
    }
  }

  private writeSession(data: { user: User; refreshToken: string }): void {
    // Only user (non-sensitive) and refreshToken are persisted.
    // The access token is intentionally excluded.
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
  }

  private setLoading(isLoading: boolean): void {
    this.authState$.next({ ...this.authState$.value, isLoading });
  }

  private setError(error: string | null): void {
    this.authState$.next({ ...this.authState$.value, error });
  }
}
